import { useEffect, useMemo, useState } from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import Autocomplete from "@/components/Autocomplete";
import Checkbox from "@/components/Checkbox";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import CardSkeleton from "@/components/Skeleton/CardSkeleton";
import SubSectionHeading from "@/components/SubSectionHeading";
import Tag from "@/components/Tag";

import { type ExperienceIndexHubState } from "./careerPlanHubSummaries";
import {
  careerPlanOnboardingExperienceIndexGapLabel,
  toggleCareerPlanOnboardingSelectedId,
  type CareerPlanOnboardingExperienceIndexGroup,
  type CareerPlanOnboardingExperienceIndexGroupId,
  type CareerPlanOnboardingExperienceIndexItem,
  type CareerPlanOnboardingFormValues
} from "./careerPlanOnboarding";
import {
  EXPERIENCE_INDEX_SCORE_MAX,
  experienceIndexScorePercent,
  formatExperienceIndexScoreValue
} from "./experienceIndexResults";

export type CareerPlanOnboardingComparisonRoleOption = {
  id: string;
  title: string;
};

export default function CareerPlanOnboardingExperienceIndexStep({
  control,
  groups,
  roleOptions,
  hubState,
  loading,
  errorMessage
}: {
  control: Control<CareerPlanOnboardingFormValues>;
  groups: CareerPlanOnboardingExperienceIndexGroup[];
  roleOptions: CareerPlanOnboardingComparisonRoleOption[];
  hubState: ExperienceIndexHubState;
  loading: boolean;
  errorMessage?: string;
}) {
  const { field } = useController({ control, name: "selectedExperienceIndexIds" });
  const { field: comparisonRole } = useController({ control, name: "comparisonRoleId" });
  const selectedRole = roleOptions.find((role) => role.id === comparisonRole.value) ?? null;
  const [query, setQuery] = useState(selectedRole?.title ?? "");
  const filteredRoles = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return roleOptions;
    return roleOptions.filter((role) => role.title.toLowerCase().includes(normalizedQuery));
  }, [query, roleOptions]);

  useEffect(() => {
    setQuery(selectedRole?.title ?? "");
  }, [selectedRole?.title]);

  if (loading) {
    return (
      <div data-testid="career-plan-onboarding-experience-index-loading">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <CardSkeleton width={100} rows={2} />
          <CardSkeleton width={100} rows={2} />
          <CardSkeleton width={100} rows={2} />
          <CardSkeleton width={100} rows={2} />
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return <ErrorBoundary variant="inline" title="Unable to Load Experience Index" message={errorMessage} />;
  }

  const selectedIds = field.value;
  const emptyCaption =
    hubState === "finished"
      ? "Experience Index results are not available yet."
      : "This person has not submitted their Experience Index assessment yet.";
  const emptyTitle = hubState === "finished" ? "No Experience Index Results" : "No Experience Index";

  return (
    <div className="flex flex-col gap-6" data-testid="career-plan-onboarding-experience-index">
      <p className="text-headline-small">
        These are the strengths and gaps from this person's Experience Index. Uncheck anything that is stale or
        off-limits for this plan.
      </p>
      {groups.length > 0 && roleOptions.length > 0 ? (
        <div
          onBlur={() => {
            setQuery(selectedRole?.title ?? "");
          }}
        >
          <Autocomplete
            data-testid="career-plan-onboarding-comparison-role"
            variant="searchableDropdown"
            label="Comparison Role"
            query={query}
            data={filteredRoles}
            menuClassName="max-h-[20rem]"
            onChangeText={setQuery}
            onSelect={(role) => {
              comparisonRole.onChange(role.id);
              setQuery(role.title);
            }}
            reset={() => {
              comparisonRole.onChange("");
              setQuery("");
            }}
            onMouseDown={(event: MouseEvent) => {
              event.preventDefault();
            }}
            renderItem={(role) => <div className="text-body-large">{role.title}</div>}
          />
        </div>
      ) : null}
      {groups.length === 0 ? (
        <div data-testid="career-plan-onboarding-experience-index-empty">
          <EmptyState iconName="quiz" title={emptyTitle} caption={emptyCaption} />
        </div>
      ) : (
        groups.map((group) => (
          <div
            className="flex flex-col gap-2"
            data-testid={`career-plan-onboarding-experience-index-${group.id}`}
            key={group.id}
          >
            <div>
              <SubSectionHeading className={group.description ? "pb-1" : undefined}>{group.label}</SubSectionHeading>
              {group.description ? (
                <p className="text-body-medium text-sys-brand-on-surface-variant">{group.description}</p>
              ) : null}
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {group.items.map((item) => (
                <ExperienceIndexSelectRow
                  key={item.id}
                  item={item}
                  groupId={group.id}
                  checked={selectedIds.includes(item.id)}
                  onToggle={() => {
                    field.onChange(toggleCareerPlanOnboardingSelectedId(selectedIds, item.id));
                  }}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function ExperienceIndexSelectRow({
  item,
  groupId,
  checked,
  onToggle
}: {
  item: CareerPlanOnboardingExperienceIndexItem;
  groupId: CareerPlanOnboardingExperienceIndexGroupId;
  checked: boolean;
  onToggle: () => void;
}) {
  const gapLabel = groupId === "focusAreas" ? careerPlanOnboardingExperienceIndexGapLabel(item.gap) : undefined;
  const testId = `career-plan-onboarding-experience-index-card-${item.id}`;
  const scorePercent = experienceIndexScorePercent(item.score);
  const targetPercent = item.target == null ? null : experienceIndexScorePercent(item.target);
  const detailText =
    item.target == null
      ? `${formatExperienceIndexScoreValue(item.score)} of ${EXPERIENCE_INDEX_SCORE_MAX}`
      : `Target ${formatExperienceIndexScoreValue(item.target)}`;
  const barFillClass = groupId === "strengths" ? "bg-extended-success-brand-color" : "bg-extended-warning-brand-color";

  const checkboxName = `career-plan-onboarding-experience-index-${item.id}`;

  return (
    <label
      className={`flex cursor-pointer items-center gap-3 rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface p-3 ${
        checked ? "" : "opacity-60"
      }`}
      data-testid={testId}
      htmlFor={checkboxName}
    >
      <Checkbox
        name={checkboxName}
        checked={checked}
        aria-label={item.label}
        onChange={(nextChecked) => {
          if (nextChecked === checked) return;
          onToggle();
        }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-title-small text-sys-brand-on-surface">{item.label}</p>
          <p className="shrink-0 text-body-small text-sys-brand-on-surface-variant">
            {formatExperienceIndexScoreValue(item.score)}
          </p>
        </div>
        <div className="relative mt-1 h-4">
          <div
            className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 overflow-hidden rounded-full bg-sys-brand-surface-container-highest"
            data-testid={`${testId}-bar`}
          >
            <div className={`h-1.5 rounded-full ${barFillClass}`} style={{ width: `${scorePercent}%` }} />
          </div>
          {targetPercent != null ? (
            <span
              className="pointer-events-none absolute top-0 z-10 h-4 w-0.5 rounded-full bg-sys-brand-on-surface ring-1 ring-sys-brand-surface"
              style={{ left: `${targetPercent}%`, transform: "translateX(-50%)" }}
              data-testid={`${testId}-target-line`}
            />
          ) : null}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-body-small text-sys-brand-on-surface-variant">{detailText}</p>
          {gapLabel ? (
            <Tag tagType="warn" tagStyle="standard" size="small">
              {gapLabel}
            </Tag>
          ) : null}
        </div>
      </div>
    </label>
  );
}
